import { useState, useMemo, useEffect } from 'react';
import { axiosFetch as authFetch } from './axiosFetch';
import type { DisplayAttribute } from '../components/display/data/types';
import { CREDENTIALS } from '../constants';

export interface DisplayUploadOptions {
  onProgress?: (percent: number) => void;
}

const REVAMP_BASE_FOR_DISPLAY = (process.env.REACT_APP_API_ENDPOINT_REVAMP || '').replace(/\/$/, '');
export const DISPLAY_BASE = (`${REVAMP_BASE_FOR_DISPLAY}/api/v1` || '/proxy-display').replace(/\/$/, '');

const trimTrailingSlash = (value: string) => value.replace(/\/$/, '');

const normalizeMimeType = (mimeType?: string) => mimeType?.split(';')[0]?.trim().toLowerCase() ?? '';

const getMediaExtensions = (mimeType?: string) => {
  const normalized = normalizeMimeType(mimeType);

  switch (normalized) {
    case 'image/jpeg':
    case 'image/jpg':
      return ['.jpg', '.jpeg'];
    case 'image/png':
      return ['.png'];
    case 'image/gif':
      return ['.gif'];
    case 'image/webp':
      return ['.webp'];
    case 'image/bmp':
      return ['.bmp'];
    case 'image/svg+xml':
      return ['.svg'];
    case 'image/avif':
      return ['.avif'];
    case 'image/apng':
      return ['.apng', '.png'];
    case 'image/heic':
      return ['.heic'];
    case 'image/heif':
      return ['.heif'];
    case 'image/tiff':
    case 'image/tif':
      return ['.tiff', '.tif'];
    case 'image/x-icon':
    case 'image/vnd.microsoft.icon':
      return ['.ico'];
    case 'video/mp4':
      return ['.mp4'];
    case 'video/quicktime':
      return ['.mov'];
    case 'video/webm':
      return ['.webm'];
    case 'video/ogg':
      return ['.ogv', '.ogg'];
    case 'video/x-msvideo':
    case 'video/avi':
      return ['.avi'];
    case 'video/x-matroska':
      return ['.mkv'];
    case 'video/mpeg':
      return ['.mpeg', '.mpg'];
    case 'video/x-ms-wmv':
      return ['.wmv'];
    case 'video/x-flv':
      return ['.flv'];
    case 'video/3gpp':
      return ['.3gp'];
    case 'video/3gpp2':
      return ['.3g2'];
    case 'video/mp2t':
      return ['.ts'];
    case 'video/x-m4v':
      return ['.m4v'];
    default:
      return [];
  }
};

const uniqueMediaUrls = (urls: Array<string | null | undefined>) =>
  Array.from(new Set(urls.filter((url): url is string => Boolean(url))));

export function getDisplayToken(): string | null {
  try {
    const raw = localStorage.getItem(CREDENTIALS);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    const cred = stored?.credentials ?? stored;
    const token =
      cred?.accessToken ??
      cred?.access_token ??
      cred?.token ??
      cred?.idToken ??
      cred?.id_token ??
      cred?.jwtToken ??
      cred?.jwt;
    return token && typeof token === 'string' && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

export function buildDisplayHeaders(token: string | null, includeJsonContentType = true): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-source': 'MP',
  };
  if (includeJsonContentType) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function messageFromDisplayErrorBody(data: any, fallbackMessage: string): string {
  return (
    data?.message ||
    data?.metaDataInfo?.responseMessage ||
    data?.error ||
    data?.data?.message ||
    fallbackMessage
  );
}

export async function parseDisplayResponse(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function displayGet(path: string) {
  const token = getDisplayToken();
  const res = await authFetch(`${DISPLAY_BASE}${path}`, { headers: buildDisplayHeaders(token) });
  const data = await parseDisplayResponse(res);
  if (!res.ok) {
    throw new Error(messageFromDisplayErrorBody(data, 'Failed to load displays'));
  }
  return { data };
}

export async function displayJson(path: string, body: object, method: 'POST' | 'PATCH' | 'DELETE' = 'POST') {
  const token = getDisplayToken();
  const res = await authFetch(`${DISPLAY_BASE}${path}`, {
    method,
    headers: buildDisplayHeaders(token),
    body: JSON.stringify(body),
  });
  const data = await parseDisplayResponse(res);
  if (!res.ok) {
    throw new Error(messageFromDisplayErrorBody(data, 'Failed to save display'));
  }
  return { data };
}

export async function displayDelete(path: string, fallbackMessage: string) {
  const token = getDisplayToken();
  const res = await authFetch(`${DISPLAY_BASE}${path}`, {
    method: 'DELETE',
    headers: buildDisplayHeaders(token),
  });
  const data = await parseDisplayResponse(res);
  if (!res.ok) {
    const conflictMessage = res.status === 409 ? 'This media is in use by a display or playlist and cannot be deleted.' : fallbackMessage;
    throw new Error(messageFromDisplayErrorBody(data, conflictMessage));
  }
  return { data };
}

/** Source of truth for Display media images: REACT_APP_IMAGE_URL + mediaId + extension (from mimeType),
 *  rather than trusting whatever `url` the API happened to return. Returns null if mediaId or the env
 *  var is missing, so callers can fall back to the API's `url` as a last resort. */
export const buildDisplayMediaSrc = (mediaId?: string | null, mimeType?: string | null): string | null => {
  if (!mediaId) return null;
  const mediaBase = process.env.REACT_APP_IMAGE_URL;
  if (!mediaBase) return null;

  const encodedId = encodeURIComponent(mediaId);
  const hasExtension = /\.[a-z0-9]+$/i.test(mediaId);
  const extension = hasExtension ? '' : getMediaExtensions(mimeType ?? undefined)[0] ?? '';

  return `${trimTrailingSlash(mediaBase)}/${encodedId}${extension}`;
};

export const getDisplayMediaUrl = (media?: Partial<DisplayAttribute['spotLightMedia'][number]> | null) => {
  const derived = buildDisplayMediaSrc(media?.mediaId, media?.mimeType);
  if (derived) return derived;
  return media?.url || media?.mediaUrl || media?.src || null;
};

export const getDisplayMediaCandidateUrls = (media?: Partial<DisplayAttribute['spotLightMedia'][number]> | null) => {
  const directUrl = media?.url || media?.mediaUrl || media?.src;
  if (!media?.mediaId) return directUrl ? [directUrl] : [];

  const mediaBase = process.env.REACT_APP_IMAGE_URL;
  if (!mediaBase) return directUrl ? [directUrl] : [];

  const mediaId = encodeURIComponent(media.mediaId);
  const baseUrl = `${trimTrailingSlash(mediaBase)}/${mediaId}`;
  const hasExtension = /\.[a-z0-9]+$/i.test(media.mediaId);

  if (hasExtension) {
    // mediaId already carries its extension — the derived URL is exact, so try it before the API's url.
    return uniqueMediaUrls([baseUrl, directUrl]);
  }

  const mimeExtensions = getMediaExtensions(media.mimeType);
  const normalizedMimeType = normalizeMimeType(media.mimeType);
  const isVideo = normalizedMimeType.startsWith('video/');
  const derivedUrl = mimeExtensions[0] ? `${baseUrl}${mimeExtensions[0]}` : null;

  return uniqueMediaUrls([
    derivedUrl,
    directUrl,
    ...mimeExtensions.map((extension) => `${baseUrl}${extension}`),
    isVideo ? `${baseUrl}.mp4` : null,
    isVideo ? `${baseUrl}.mov` : null,
    isVideo ? `${baseUrl}.webm` : null,
    isVideo ? `${baseUrl}.avi` : null,
    !isVideo ? `${baseUrl}.jpg` : null,
    !isVideo ? `${baseUrl}.jpeg` : null,
    !isVideo ? `${baseUrl}.png` : null,
    !isVideo ? `${baseUrl}.webp` : null,
    !isVideo ? `${baseUrl}.gif` : null,
    !isVideo ? `${baseUrl}.bmp` : null,
    !isVideo ? `${baseUrl}.svg` : null,
    !isVideo ? `${baseUrl}.avif` : null,
    !isVideo ? `${baseUrl}.heic` : null,
    !isVideo ? `${baseUrl}.heif` : null,
  ]);
};

export function useDisplayMediaPreviewSource(media?: { mediaId?: string; mimeType?: string; url?: string; mediaUrl?: string; src?: string } | null) {
  const mediaId = media?.mediaId;
  const mimeType = media?.mimeType;
  const url = media?.url;
  const directMediaUrl = media?.mediaUrl;
  const src = media?.src;
  const candidateUrls = useMemo(
    () => getDisplayMediaCandidateUrls({ mediaId, mimeType, url, mediaUrl: directMediaUrl, src }),
    [mediaId, mimeType, url, directMediaUrl, src],
  );
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setCurrentUrlIndex(0);
    setFailed(false);
  }, [candidateUrls]);

  const mediaUrl = failed ? null : candidateUrls[currentUrlIndex] ?? null;

  const handleMediaError = () => {
    if (currentUrlIndex < candidateUrls.length - 1) {
      setCurrentUrlIndex((index) => index + 1);
      return;
    }

    setFailed(true);
  };

  return { mediaUrl, failed, handleMediaError };
}
